import React, { useState } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Badge } from '../ui/Badge';
import { Spinner } from '../ui/Spinner';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Calendar, 
  Clock, 
  Newspaper,
  CheckCircle,
  X,
  ExternalLink,
  Filter,
  RefreshCw
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../../api/worker';
import { motion, AnimatePresence } from 'motion/react';
import { RichContentViewer } from '../RichContentViewer';
import { cn } from '../../lib/utils';
import { toast } from 'react-hot-toast';

interface Article {
  id: string;
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  content: string;
  date: string;
  readTime: string;
  published: boolean;
}

export const AdminNewsroomManager: React.FC = () => {
  const { isSuperAdmin, isSupportAdmin } = usePermissions();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write');

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: 'Product Update',
    excerpt: '',
    content: '',
    readTime: '3 min read',
    published: true,
  });

  const categories = ['Product Update', 'Governance', 'Security', 'Best Practices'];

  const { data: articles = [], isLoading, refetch, isFetching } = useQuery<Article[]>({
    queryKey: ['admin', 'articles'],
    queryFn: async () => {
      const res = await apiWorker.get('/api/admin/articles');
      return Array.isArray(res.data) ? res.data : (res.data?.content || []);
    },
    enabled: isSuperAdmin || isSupportAdmin
  });

  const createMutation = useMutation({
    mutationFn: async (newArticle: any) => {
      const res = await apiWorker.post('/api/admin/articles', newArticle);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Newsroom article published');
      queryClient.invalidateQueries({ queryKey: ['admin', 'articles'] });
      queryClient.invalidateQueries({ queryKey: ['public', 'articles'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('Failed to create article')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiWorker.put(`/api/admin/articles/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Article updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin', 'articles'] });
      queryClient.invalidateQueries({ queryKey: ['public', 'articles'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('Failed to update article')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiWorker.delete(`/api/admin/articles/${id}`);
    },
    onSuccess: () => {
      toast.success('Article deleted');
      queryClient.invalidateQueries({ queryKey: ['admin', 'articles'] });
      queryClient.invalidateQueries({ queryKey: ['public', 'articles'] });
    },
    onError: () => toast.error('Failed to delete article')
  });

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    const generatedSlug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
    
    setFormData((prev) => ({
      ...prev,
      title,
      slug: editingArticle ? prev.slug : generatedSlug
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      category: 'Product Update',
      excerpt: '',
      content: '',
      readTime: '3 min read',
      published: true,
    });
    setEditingArticle(null);
    setActiveTab('write');
  };

  const handleOpenCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (article: Article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      slug: article.slug,
      category: article.category,
      excerpt: article.excerpt,
      content: article.content,
      readTime: article.readTime,
      published: article.published,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.slug) return;

    if (editingArticle) {
      updateMutation.mutate({ id: editingArticle.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to permanently delete this article?')) {
      deleteMutation.mutate(id);
    }
  };

  const filteredArticles = articles.filter(art => {
    if (categoryFilter !== 'ALL' && art.category !== categoryFilter) return false;
    if (statusFilter === 'PUBLISHED' && !art.published) return false;
    if (statusFilter === 'DRAFTS' && art.published) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchesTitle = art.title?.toLowerCase().includes(q);
      const matchesCat = art.category?.toLowerCase().includes(q);
      const matchesExcerpt = art.excerpt?.toLowerCase().includes(q);
      const matchesSlug = art.slug?.toLowerCase().includes(q);
      if (!matchesTitle && !matchesCat && !matchesExcerpt && !matchesSlug) return false;
    }
    return true;
  });

  const publishedCount = articles.filter(a => a.published).length;
  const draftsCount = articles.filter(a => !a.published).length;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header & Primary CTA */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-[#0044FF]" />
            Global Newsroom Manager
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Publish public governance insights, architectural releases, and platform announcements.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleOpenCreate}
            className="gap-2 bg-black text-white hover:bg-[#0044FF] border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] text-xs font-bold uppercase tracking-wider px-4 py-2"
          >
            <Plus size={15} /> New Article
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            className="border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] gap-1.5 text-xs font-bold uppercase tracking-wider"
            disabled={isFetching}
          >
            <RefreshCw size={13} className={cn(isFetching ? "animate-spin" : "")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bauhaus Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-t-4 border-t-blue-600 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Total Articles</span>
            <Newspaper className="w-4 h-4 text-blue-500" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{articles.length}</div>
        </Card>

        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-t-4 border-t-green-600 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Published Live</span>
            <CheckCircle className="w-4 h-4 text-green-500" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{publishedCount}</div>
        </Card>

        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-t-4 border-t-yellow-500 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Drafts Pending</span>
            <EyeOff className="w-4 h-4 text-yellow-600" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{draftsCount}</div>
        </Card>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-white dark:bg-slate-900 border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
        <div className="flex items-center gap-3 flex-wrap flex-1">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search newsroom stories..."
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-white dark:bg-black border-2 border-black dark:border-white rounded-none outline-none focus:ring-2 focus:ring-[#0044FF] text-black dark:text-white"
            />
          </div>

          <div className="flex items-center gap-1.5 text-xs font-mono font-bold uppercase text-gray-600 dark:text-gray-400">
            <Filter size={13} />
            Filters:
          </div>

          <div className="flex items-center gap-1">
            <label className="text-[11px] font-mono text-gray-500 uppercase">Category:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="text-xs font-mono font-bold border border-black dark:border-white bg-white dark:bg-slate-950 px-2 py-1 rounded-none outline-none cursor-pointer"
            >
              <option value="ALL">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <label className="text-[11px] font-mono text-gray-500 uppercase">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs font-mono font-bold border border-black dark:border-white bg-white dark:bg-slate-950 px-2 py-1 rounded-none outline-none cursor-pointer"
            >
              <option value="ALL">All Statuses</option>
              <option value="PUBLISHED">Published Only</option>
              <option value="DRAFTS">Drafts Only</option>
            </select>
          </div>
        </div>

        {(categoryFilter !== 'ALL' || statusFilter !== 'ALL' || searchQuery.trim()) && (
          <button
            type="button"
            onClick={() => {
              setCategoryFilter('ALL');
              setStatusFilter('ALL');
              setSearchQuery('');
            }}
            className="text-xs font-mono font-bold text-red-600 dark:text-red-400 hover:underline uppercase"
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* Article List */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Spinner size="lg" />
          <p className="text-xs font-mono uppercase tracking-widest text-gray-500 mt-4">Loading newsroom stories...</p>
        </div>
      ) : filteredArticles.length > 0 ? (
        <div className="space-y-4">
          {filteredArticles.map((art) => (
            <div
              key={art.id}
              className="p-5 bg-white dark:bg-slate-900 border-2 border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-all hover:translate-x-0.5"
            >
              <div className="space-y-2 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="blue" className="rounded-none border border-black dark:border-white font-mono uppercase text-[10px] font-bold">
                    {art.category}
                  </Badge>
                  <span className="text-xs text-gray-500 flex items-center gap-1 font-mono">
                    <Calendar size={12} /> {art.date}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center gap-1 font-mono">
                    <Clock size={12} /> {art.readTime}
                  </span>
                  {art.published ? (
                    <span className="flex items-center gap-1 text-[10px] font-mono font-bold text-green-700 bg-green-100 dark:bg-green-950/40 px-2 py-0.5 border border-green-600 uppercase">
                      <Eye size={11} /> Published
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] font-mono font-bold text-gray-700 bg-gray-100 dark:bg-slate-800 px-2 py-0.5 border border-black/20 uppercase">
                      <EyeOff size={11} /> Draft
                    </span>
                  )}
                </div>

                <h3 className="font-display font-bold text-lg text-gray-900 dark:text-white leading-snug">
                  {art.title}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 font-sans line-clamp-2 max-w-3xl">
                  {art.excerpt}
                </p>

                <div className="flex items-center gap-2 text-xs font-mono pt-1">
                  <span className="text-gray-500">Public Route:</span>
                  <a
                    href={`/newsroom/${art.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold text-[#0044FF] hover:underline flex items-center gap-1"
                  >
                    /newsroom/{art.slug}
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 border-t md:border-t-0 pt-3 md:pt-0 w-full md:w-auto justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleOpenEdit(art)}
                  className="border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] gap-1.5 text-xs font-bold uppercase tracking-wider"
                >
                  <Edit size={13} /> Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(art.id)}
                  className="border-2 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 p-2 rounded-none"
                  title="Delete Article"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="flex flex-col items-center justify-center p-12 text-center border-2 border-black dark:border-white bg-white dark:bg-slate-900 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <div className="w-16 h-16 bg-[#FFD600] border-2 border-black flex items-center justify-center mb-4 shadow-[2px_2px_0px_#000000]">
            <Newspaper size={32} className="text-black" />
          </div>
          <h3 className="font-display font-black text-xl uppercase tracking-tight text-gray-900 dark:text-white">
            No Newsroom Articles Found
          </h3>
          <p className="text-xs text-gray-500 max-w-sm mt-2 font-sans">
            Try adjusting your search criteria or create a new announcement.
          </p>
          <Button
            onClick={handleOpenCreate}
            className="mt-4 gap-2 bg-black text-white hover:bg-[#0044FF] border-2 border-black font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-none"
          >
            <Plus size={15} /> Create First Article
          </Button>
        </Card>
      )}

      {/* Bauhaus Create / Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
            <motion.div 
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              className="bg-white dark:bg-slate-900 w-full max-w-3xl max-h-[92vh] rounded-none flex flex-col border-4 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] overflow-hidden"
            >
              <header className="px-6 py-4 bg-black text-white dark:bg-white dark:text-black flex items-center justify-between border-b-2 border-black dark:border-white">
                <h3 className="font-display font-black text-base uppercase tracking-wider flex items-center gap-2">
                  <Newspaper size={18} />
                  {editingArticle ? 'Edit Newsroom Article' : 'Compose Newsroom Article'}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="text-white dark:text-black hover:opacity-70 cursor-pointer p-1"
                >
                  <X size={20} />
                </button>
              </header>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider font-mono">Article Title *</label>
                  <Input 
                    placeholder="e.g. Celebrating Our Initial Launch" 
                    value={formData.title} 
                    onChange={handleTitleChange}
                    className="rounded-none border-2 border-black dark:border-white"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider font-mono">URL Slug *</label>
                    <Input 
                      placeholder="e.g. celebrating-launch" 
                      value={formData.slug} 
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      className="rounded-none border-2 border-black dark:border-white font-mono text-xs"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider font-mono">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full h-[40px] px-3 py-2 text-xs font-mono font-bold border-2 border-black dark:border-white rounded-none outline-none focus:ring-2 focus:ring-[#0044FF] bg-white dark:bg-slate-900"
                    >
                      {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider font-mono">Read Time</label>
                    <Input 
                      placeholder="e.g. 3 min read" 
                      value={formData.readTime} 
                      onChange={(e) => setFormData(prev => ({ ...prev, readTime: e.target.value }))}
                      className="rounded-none border-2 border-black dark:border-white font-mono text-xs"
                    />
                  </div>

                  <div className="space-y-1 flex flex-col justify-end">
                    <label className="flex items-center gap-2 cursor-pointer p-2.5 bg-gray-50 dark:bg-slate-950 border-2 border-black dark:border-white">
                      <input 
                        type="checkbox" 
                        checked={formData.published} 
                        onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
                        className="rounded-none border-2 border-black dark:border-white text-blue-600 h-4 w-4"
                      />
                      <span className="text-xs font-mono font-bold uppercase text-gray-900 dark:text-white">Publish Immediately</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider font-mono">Card Excerpt *</label>
                  <Textarea 
                    placeholder="Short summary for search engines and preview cards..." 
                    value={formData.excerpt} 
                    onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                    className="rounded-none border-2 border-black dark:border-white text-xs"
                    rows={2}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between border-b-2 border-black dark:border-white pb-2">
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider font-mono">Markdown Content *</label>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setActiveTab('write')}
                        className={cn(
                          "px-3 py-1 text-xs font-mono font-bold uppercase border-2 rounded-none transition cursor-pointer",
                          activeTab === 'write'
                            ? "bg-black text-white dark:bg-white dark:text-black border-black dark:border-white"
                            : "border-transparent text-gray-500 hover:text-black dark:hover:text-white"
                        )}
                      >
                        Write
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('preview')}
                        className={cn(
                          "px-3 py-1 text-xs font-mono font-bold uppercase border-2 rounded-none transition cursor-pointer",
                          activeTab === 'preview'
                            ? "bg-black text-white dark:bg-white dark:text-black border-black dark:border-white"
                            : "border-transparent text-gray-500 hover:text-black dark:hover:text-white"
                        )}
                      >
                        Live Preview
                      </button>
                    </div>
                  </div>

                  {activeTab === 'write' ? (
                    <textarea 
                      placeholder="Write article content using standard Markdown formatting..." 
                      value={formData.content} 
                      onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                      className="w-full p-3 border-2 border-black dark:border-white rounded-none text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#0044FF] bg-white dark:bg-slate-900"
                      rows={10}
                      required
                    />
                  ) : (
                    <div className="w-full p-4 border-2 border-black dark:border-white rounded-none max-h-[250px] overflow-y-auto bg-gray-50 dark:bg-slate-950 font-sans">
                      {formData.content ? (
                        <RichContentViewer content={formData.content} className="prose-sm dark:prose-invert" />
                      ) : (
                        <p className="text-xs text-gray-400 font-mono italic">Enter markdown content in the Write tab to see preview.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t-2 border-black dark:border-white flex justify-end gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => setIsModalOpen(false)}
                    type="button"
                    className="border-2 border-black dark:border-white rounded-none text-xs font-bold uppercase"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-black text-white hover:bg-[#0044FF] border-2 border-black dark:border-white font-bold uppercase tracking-wider text-xs px-6 py-2.5 rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {createMutation.isPending || updateMutation.isPending ? (
                      <span className="flex items-center gap-1.5"><Spinner size="sm" /> Saving...</span>
                    ) : (
                      editingArticle ? 'Save Changes' : 'Publish Article'
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
